import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';

// Module scope, because the header and the bars below it are mounted as separate Vue apps.
const headerBottom = ref(0);
const visibleBars = reactive(new Set());

export function useHeaderStackedBarState(headerRef) {
    let resizeObserver = null;

    const updateHeaderBottom = () => {
        headerBottom.value = Math.max(0, Math.round(headerRef.value?.getBoundingClientRect().bottom ?? 0));
    };

    onMounted(() => {
        updateHeaderBottom();
        if (headerRef.value) {
            resizeObserver = new ResizeObserver(updateHeaderBottom);
            resizeObserver.observe(headerRef.value);
        }
    });

    onBeforeUnmount(() => {
        resizeObserver?.disconnect();
    });

    return { hasVisibleStackedBar: computed(() => visibleBars.size > 0) };
}

export function useHeaderStackedBar(barRef) {
    const bar = Symbol('stacked bar');
    let intersectionObserver = null;

    const setVisible = isVisible => {
        if (isVisible) {
            visibleBars.add(bar);
        }
        else {
            visibleBars.delete(bar);
        }
    };

    const onIntersection = ([entry]) => setVisible(entry.isIntersecting);

    const observe = () => {
        intersectionObserver?.disconnect();
        intersectionObserver = null;
        if (!barRef.value || !headerBottom.value) {
            return;
        }

        // The bar counts as visible while any of it shows below the header.
        intersectionObserver = new IntersectionObserver(onIntersection, { rootMargin: `-${headerBottom.value}px 0px 0px 0px` });
        intersectionObserver.observe(barRef.value);
    };

    onMounted(() => {
        setVisible(!!barRef.value && barRef.value.getBoundingClientRect().bottom > headerBottom.value);
        observe();
    });
    watch(headerBottom, observe);

    onBeforeUnmount(() => {
        intersectionObserver?.disconnect();
        setVisible(false);
    });
}
